import { useState, useCallback, useEffect } from 'react';
import { DebtProductRowData, SecurityOfferedItem } from './debtProductTypes';

// Type for row hover state - tracks which row's editable fields should show active state
export type HoveredRowData = {
    rowKey: string; // Unique key for the row (product_<productId> or lender_<lenderId>_<productId>)
    isParentRow: boolean;
} | null;

// Type for security modal context
export type SecurityModalData = {
    securityData: SecurityOfferedItem[];
    productId: string;
    productName: string;
    lenderId?: string;
    isParentRow: boolean;
    pecSecurity?: string[][];
    mandateId?: string;
} | null;

// Config for unsaved changes modal handlers
export type UnsavedChangesConfig = {
    checkForUnsavedChanges: () => boolean;
};

/**
 * Custom hook to manage UI states including loading, transitions, and animations
 * - Tracks which rows are currently calculating probability
 * - Manages modal transition states for seamless animations
 * - Manages unsaved changes modal state and handlers
 */
const useUIStateLogic = (unsavedChangesConfig?: UnsavedChangesConfig) => {
    // Track rows currently calculating probability (Set of keys like "product_<productId>" or "lender_<lenderId>_<productId>")
    const [calculatingRowIds, setCalculatingRowIds] = useState<Set<string>>(new Set());

    // Track rows showing shimmer effect when parent product is being edited (Set of keys like "lender_<lenderId>_<productId>")
    const [shimmeringRowIds, setShimmeringRowIds] = useState<Set<string>>(new Set());

    // Track modal transition state for seamless animations between modals
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Track share success modal visibility
    const [showShareSuccessModal, setShowShareSuccessModal] = useState(false);

    // Track security modal visibility and data
    const [showSecurityModal, setShowSecurityModal] = useState<SecurityModalData>(null);

    // Track unsaved changes modal visibility
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

    // Unsaved changes modal - pending action state
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    // Track mandate sharing drawer visibility
    const [isMandateSharingDrawerOpen, setIsMandateSharingDrawerOpen] = useState(false);

    // Track share with lender modal visibility
    const [isShareWithLenderModalOpen, setIsShareWithLenderModalOpen] = useState(false);

    // Track which row is being hovered for editable fields activation
    const [hoveredEditableRow, setHoveredEditableRow] = useState<HoveredRowData>(null);

    // Track pending hover for debouncing
    const [pendingHover, setPendingHover] = useState<HoveredRowData>(null);

    // Debounce hover state changes to prevent flickering
    useEffect(() => {
        if (pendingHover === null) {
            setHoveredEditableRow(null);
            return;
        }

        if (hoveredEditableRow?.rowKey === pendingHover.rowKey) return;

        const timeoutId = setTimeout(() => {
            setHoveredEditableRow(pendingHover);
        }, 50);

        return () => clearTimeout(timeoutId);
    }, [pendingHover, hoveredEditableRow]);

    /**
     * Helper to generate unique row key for calculating state
     * Product rows: "product_<productId>"
     * Lender rows: "lender_<lenderId>_<productId>"
     */
    const getCalculatingRowKey = useCallback((rowData: DebtProductRowData, isParentRow: boolean): string => {
        if (isParentRow) {
            return `product_${rowData.productId}`;
        }
        return `lender_${rowData.lenderId}_${rowData.productId}`;
    }, []);

    /**
     * Check if a row is currently calculating probability
     */
    const isRowCalculating = useCallback(
        (rowData: DebtProductRowData, isParentRow: boolean): boolean => {
            const key = getCalculatingRowKey(rowData, isParentRow);
            return calculatingRowIds.has(key);
        },
        [calculatingRowIds, getCalculatingRowKey],
    );

    /**
     * Add row keys to calculating state
     */
    const addToCalculatingState = useCallback((keys: string[]) => {
        setCalculatingRowIds((prev) => {
            const newSet = new Set(prev);
            keys.forEach((key) => newSet.add(key));
            return newSet;
        });
    }, []);

    /**
     * Remove row keys from calculating state based on API response
     */
    const removeFromCalculatingState = useCallback((responseData: any[], aggregatorId: string) => {
        if (!responseData || !Array.isArray(responseData)) return;

        setCalculatingRowIds((prev) => {
            const newSet = new Set(prev);
            responseData.forEach((item) => {
                if (item.lenderId === aggregatorId) {
                    // Product row
                    newSet.delete(`product_${item.productId}`);
                } else {
                    // Lender row
                    newSet.delete(`lender_${item.lenderId}_${item.productId}`);
                }
            });
            return newSet;
        });
    }, []);

    /**
     * Clear a specific row from calculating state by key
     */
    const clearCalculatingRow = useCallback((key: string) => {
        setCalculatingRowIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
        });
    }, []);

    /**
     * Clear multiple rows from calculating state by keys
     */
    const clearCalculatingRows = useCallback((keys: string[]) => {
        setCalculatingRowIds((prev) => {
            const newSet = new Set(prev);
            keys.forEach((key) => newSet.delete(key));
            return newSet;
        });
    }, []);

    /**
     * Check if a row is currently showing shimmer effect
     */
    const isRowShimmering = useCallback(
        (rowData: DebtProductRowData, isParentRow: boolean): boolean => {
            const key = getCalculatingRowKey(rowData, isParentRow);
            return shimmeringRowIds.has(key);
        },
        [shimmeringRowIds, getCalculatingRowKey],
    );

    /**
     * Add row keys to shimmering state
     */
    const addToShimmeringState = useCallback((keys: string[]) => {
        setShimmeringRowIds((prev) => {
            const newSet = new Set(prev);
            keys.forEach((key) => newSet.add(key));
            return newSet;
        });
    }, []);

    /**
     * Remove row keys from shimmering state based on API response
     */
    const removeFromShimmeringState = useCallback((responseData: any[], aggregatorId: string) => {
        if (!responseData || !Array.isArray(responseData)) return;

        setShimmeringRowIds((prev) => {
            const newSet = new Set(prev);
            responseData.forEach((item) => {
                if (item.lenderId === aggregatorId) {
                    // Product row
                    newSet.delete(`product_${item.productId}`);
                } else {
                    // Lender row
                    newSet.delete(`lender_${item.lenderId}_${item.productId}`);
                }
            });
            return newSet;
        });
    }, []);

    /**
     * Clear multiple rows from shimmering state by keys
     */
    const clearShimmeringRows = useCallback((keys: string[]) => {
        setShimmeringRowIds((prev) => {
            const newSet = new Set(prev);
            keys.forEach((key) => newSet.delete(key));
            return newSet;
        });
    }, []);

    /**
     * Handler that checks for unsaved changes before executing an action
     * @param action - The action to execute
     */
    const handleActionWithUnsavedCheck = useCallback(
        (action: () => void) => {
            if (unsavedChangesConfig?.checkForUnsavedChanges()) {
                setPendingAction(() => action);
                setShowUnsavedChangesModal(true);
            } else {
                action();
            }
        },
        [unsavedChangesConfig],
    );

    /**
     * Close without saving - just close modal and execute pending action
     */
    const handleCloseWithoutSaving = useCallback(() => {
        setShowUnsavedChangesModal(false);
        if (pendingAction) {
            pendingAction();
            setPendingAction(null);
        }
    }, [pendingAction]);

    /**
     * Save changes - save first, then execute pending action
     * @param saveFunction - Async function to save changes
     */
    const handleSaveChanges = useCallback(
        async (saveFunction: () => Promise<void>) => {
            await saveFunction();
            setShowUnsavedChangesModal(false);
            if (pendingAction) {
                pendingAction();
                setPendingAction(null);
            }
        },
        [pendingAction],
    );

    /**
     * Close modal without any action
     */
    const handleCloseUnsavedModal = useCallback(() => {
        setShowUnsavedChangesModal(false);
        setPendingAction(null);
    }, []);

    /**
     * Check if there's a pending action waiting to be executed
     * Used to skip certain operations (like setting selectedMandate) when saving before switching mandates
     */
    const hasPendingAction = useCallback(() => {
        return pendingAction !== null;
    }, [pendingAction]);

    /**
     * Set the hovered row for editable fields activation with debouncing
     * When hovering over any editable field in a row, all editable fields in that row should show active state
     * Uses state-based debouncing to prevent flickering when cursor is between rows
     */
    const setEditableRowHover = useCallback(
        (rowData: DebtProductRowData | null, isParentRow: boolean) => {
            if (!rowData) {
                setPendingHover(null);
                return;
            }

            const rowKey = getCalculatingRowKey(rowData, isParentRow);
            const newHoverData = { rowKey, isParentRow };

            setPendingHover(newHoverData);
        },
        [getCalculatingRowKey],
    );

    /**
     * Check if a row's editable fields should show active/hover state
     */
    const isEditableRowHovered = useCallback(
        (rowData: DebtProductRowData, isParentRow: boolean): boolean => {
            if (!hoveredEditableRow) return false;
            const rowKey = getCalculatingRowKey(rowData, isParentRow);
            return hoveredEditableRow.rowKey === rowKey;
        },
        [hoveredEditableRow, getCalculatingRowKey],
    );

    return {
        // Row calculating states
        calculatingRowIds,
        getCalculatingRowKey,
        isRowCalculating,
        addToCalculatingState,
        removeFromCalculatingState,
        clearCalculatingRow,
        clearCalculatingRows,
        // Row shimmering states
        shimmeringRowIds,
        isRowShimmering,
        addToShimmeringState,
        removeFromShimmeringState,
        clearShimmeringRows,
        // Modal transition states
        isTransitioning,
        setIsTransitioning,
        // Share success modal state
        showShareSuccessModal,
        setShowShareSuccessModal,
        // Security modal state
        showSecurityModal,
        setShowSecurityModal,
        // Unsaved changes modal state
        showUnsavedChangesModal,
        setShowUnsavedChangesModal,
        // Unsaved changes modal handlers
        handleActionWithUnsavedCheck,
        handleCloseWithoutSaving,
        handleSaveChanges,
        handleCloseUnsavedModal,
        hasPendingAction,
        // Mandate sharing drawer state
        isMandateSharingDrawerOpen,
        setIsMandateSharingDrawerOpen,
        // Share with lender modal state
        isShareWithLenderModalOpen,
        setIsShareWithLenderModalOpen,
        // Row hover state for editable fields
        hoveredEditableRow,
        setEditableRowHover,
        isEditableRowHovered,
    };
};

export default useUIStateLogic;
